import { keyBy, merge } from 'lodash';
import { useCallback } from 'react';
import {
  QueryKey,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { ResourceManagmentApi } from 'api/resource-management';
import {
  ResourceType,
  AvailabilityForTeamWithProject,
  DeleteAllocationsProps,
  AllocationsGroupByUsersOrTeamsLookup,
  AllocationsGroupByUsersOrTeams,
  AllocationsGroupByUser,
  AllocationsGroupByTeamLookup,
  AllocationsGroupByTeam,
  AllocationsGroupByUserLookup,
  ConvertAllocationsPropsApi,
  ResourceManagementPermissions,
  ResourceManagementPermissionsResponse,
  RMPermissionsApiParamType,
} from 'api/resource-management/types';

// * Moving this function to id.ts and importing from there throws some error in ckEditor
export const aggregationIdForUser = (
  projectId: number,
  roleId?: number,
  referenceTeamId?: number,
): string => {
  const rolePart = roleId ? `_role-${roleId}` : '';
  const referenceTeamPart = referenceTeamId
    ? `_referenceTeam-${referenceTeamId}`
    : '';
  const aggregationId = `project-${projectId}${rolePart}${referenceTeamPart}`;

  return aggregationId;
};

const allocationByUserToLookup = ({
  aggregations,
  ...rest
}: AllocationsGroupByUser): AllocationsGroupByUserLookup => ({
  ...rest,
  aggregationLookup: keyBy(
    aggregations,
    ({ projectId, roleId, referenceTeamId }) =>
      aggregationIdForUser(projectId, roleId, referenceTeamId),
  ),
});

const allocationByTeamToLookup = ({
  aggregations,
  ...rest
}: AllocationsGroupByTeam): AllocationsGroupByTeamLookup => ({
  ...rest,
  aggregationLookup: keyBy(
    aggregations,
    ({ projectId }) => `project-${projectId}`,
  ),
});

export const allocationGroupedByPeopleToLookup = (
  data: AllocationsGroupByUsersOrTeams,
): AllocationsGroupByUsersOrTeamsLookup =>
  data.resources.reduce((result, resource) => {
    /**
     * There will be multiple records of same user each corresponding to an instance in RM
     * Hence merging the result
     * */
    if (resource.type === ResourceType.User) {
      return merge(result, {
        [`user-${resource.user.userId}`]: allocationByUserToLookup(resource),
      });
    }

    return merge(result, {
      [`team-${resource.team.teamId}`]: allocationByTeamToLookup(resource),
    });
  }, {});

export const getAllocationAggregationKey = (): QueryKey => [
  'allocations',
  'aggregations',
];

export const getResourceAllocationsForPeople = (): QueryKey => [
  'allocations',
  'people',
];

export type ResourceAvailabilityForTeamQueryKey = [string, number, number];

export const getResourceAvailabilityForTeamQueryKey = (
  projectId: number,
  teamId: number,
): ResourceAvailabilityForTeamQueryKey => [
  'resource-availability',
  projectId,
  teamId,
];

export const useResourceAvailabilityForTeam = (
  projectId: number,
  teamId: number,
  queryOptions: UseQueryOptions<
    AvailabilityForTeamWithProject,
    unknown,
    AvailabilityForTeamWithProject,
    ResourceAvailabilityForTeamQueryKey
  > = {},
): UseQueryResult<AvailabilityForTeamWithProject> =>
  useQuery(
    getResourceAvailabilityForTeamQueryKey(projectId, teamId),
    () => ResourceManagmentApi.getResourceAvailaibility(projectId, teamId),
    {
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      ...queryOptions,
    },
  );

export const useResourceAvailabilityForTeamPlanner = (
  {
    projectId,
    teamId,
    startDate,
    endDate,
    allocatedMinutes,
    roleId,
    teamPlanner,
  },
  queryOptions: UseQueryOptions<
    AvailabilityForTeamWithProject,
    unknown,
    AvailabilityForTeamWithProject,
    ResourceAvailabilityForTeamQueryKey
  > = {},
): UseQueryResult<AvailabilityForTeamWithProject> =>
  useQuery(
    getResourceAvailabilityForTeamQueryKey(projectId, teamId),
    () =>
      ResourceManagmentApi.getResourceAvailaibilityForTeamPlanner({
        projectId,
        teamId,
        startDate,
        endDate,
        allocatedMinutes,
        roleId,
        teamPlanner,
      }),
    {
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      ...queryOptions,
    },
  );

export const useDeleteResourceAllocationsQuery = (
  options: UseMutationOptions<void, unknown, DeleteAllocationsProps> = {},
): UseMutationResult<void, unknown, DeleteAllocationsProps> =>
  useMutation(
    (params) => ResourceManagmentApi.deleteResourceAllocations(params),
    options,
  );

export const useConvertResourceAllocationsQuery = (
  options: UseMutationOptions<void, unknown, ConvertAllocationsPropsApi> = {},
): UseMutationResult<void, unknown, ConvertAllocationsPropsApi> =>
  useMutation(
    (params) => ResourceManagmentApi.convertResourceAllocations(params),
    options,
  );

export const useAllocationMinutesForProjectForWeek = () => {
  const dailyMinutesForProject = useCallback(
    (projectIdValue: number[], startDate: string, userId: number) =>
      ResourceManagmentApi.getDailyMinutesForProject(
        startDate,
        userId,
        projectIdValue,
      ),
    [],
  );

  return {
    dailyMinutesForProject,
  };
};

export const getResourceManagementPermissionsKey = [
  'resource-management-permissions',
];

export const useResourceManagementPermissions = (
  queryOptions: UseQueryOptions<
    ResourceManagementPermissions,
    unknown,
    ResourceManagementPermissions,
    typeof getResourceManagementPermissionsKey
  > = {},
): UseQueryResult<ResourceManagementPermissions> =>
  useQuery(
    getResourceManagementPermissionsKey,
    () => ResourceManagmentApi.getResourceMangementPermissions(),
    {
      ...queryOptions,
    },
  );

type AccessibleUsersFromRMKeyType = ['accessible-users-rm', string];

const generateAccessibleUsersFromRMQueryKey = (
  extraQueryParams = {},
): AccessibleUsersFromRMKeyType => {
  return ['accessible-users-rm', JSON.stringify(extraQueryParams)];
};

const queryParamsForAccesibleUsersForRM = {
  source: RMPermissionsApiParamType.ResourceManagement,
};

export const useAccesiblePeopleFromRM = (
  queryOptions: UseQueryOptions<
    ResourceManagementPermissionsResponse,
    unknown,
    ResourceManagementPermissionsResponse,
    AccessibleUsersFromRMKeyType
  > = {},
): UseQueryResult<ResourceManagementPermissionsResponse> =>
  useQuery(
    generateAccessibleUsersFromRMQueryKey(queryParamsForAccesibleUsersForRM),
    () =>
      ResourceManagmentApi.getAccesibleUsersFromRM(
        queryParamsForAccesibleUsersForRM,
      ),
    {
      ...queryOptions,
    },
  );

type AccessibleUsersFromResourceAIKeyType = [
  'accessible-users-resource-ai',
  string,
];

const generateAccessibleUsersFromResourceAiQueryKey = (
  extraQueryParams = {},
): AccessibleUsersFromResourceAIKeyType => {
  return ['accessible-users-resource-ai', JSON.stringify(extraQueryParams)];
};

const queryParamsForAccesibleUsersForResourceAI = {
  source: RMPermissionsApiParamType.ResourceAI,
};

export const useAccesiblePeopleFromResourceAI = (
  queryOptions: UseQueryOptions<
    ResourceManagementPermissionsResponse,
    unknown,
    ResourceManagementPermissionsResponse,
    AccessibleUsersFromResourceAIKeyType
  > = {},
): UseQueryResult<ResourceManagementPermissionsResponse> =>
  useQuery(
    generateAccessibleUsersFromResourceAiQueryKey(
      queryParamsForAccesibleUsersForResourceAI,
    ),
    () =>
      ResourceManagmentApi.getAccesibleUsersFromRM(
        queryParamsForAccesibleUsersForResourceAI,
      ),
    {
      ...queryOptions,
    },
  );

type AccessibleProjectsFromRMKeyType = ['accessible-projects-rm', string];

const generateAccessibleProjectsFromRMQueryKey = (
  extraQueryParams = {},
): AccessibleProjectsFromRMKeyType => {
  return ['accessible-projects-rm', JSON.stringify(extraQueryParams)];
};

const queryParamsForAccesibleProjectsForRM = {
  source: RMPermissionsApiParamType.ResourceManagement,
};

export const useAccesibleProjectsFromRM = (
  queryOptions: UseQueryOptions<
    ResourceManagementPermissionsResponse,
    unknown,
    ResourceManagementPermissionsResponse,
    AccessibleProjectsFromRMKeyType
  > = {},
): UseQueryResult<ResourceManagementPermissionsResponse> =>
  useQuery(
    generateAccessibleProjectsFromRMQueryKey(
      queryParamsForAccesibleProjectsForRM,
    ),
    () =>
      ResourceManagmentApi.getAccesibleProjectsFromRM(
        queryParamsForAccesibleProjectsForRM,
      )
        .then((data) => ({ ...data, '-1': { canManage: false } }))
        .catch((error) => error),
    {
      ...queryOptions,
    },
  );

type AccessibleProjectsFromResourceAIKeyType = [
  'accessible-projects-resource-ai',
  string,
];

const generateAccessibleProjectsFromResourceAiQueryKey = (
  extraQueryParams = {},
): AccessibleProjectsFromResourceAIKeyType => {
  return ['accessible-projects-resource-ai', JSON.stringify(extraQueryParams)];
};

const queryParamsForAccesibleProjectsForResourceAI = {
  source: RMPermissionsApiParamType.ResourceAI,
};

export const useAccesibleProjectsForResourceAI = (
  queryOptions: UseQueryOptions<
    number[],
    unknown,
    number[],
    AccessibleProjectsFromResourceAIKeyType
  > = {},
): UseQueryResult<number[]> =>
  useQuery(
    generateAccessibleProjectsFromResourceAiQueryKey(
      queryParamsForAccesibleProjectsForResourceAI,
    ),
    () => ResourceManagmentApi.getAccesibleProjectsForResourceAI(),
    {
      ...queryOptions,
    },
  );

type AccessiblePeopleFromResourceAIKeyType = [
  'accessible-people-resource-ai',
  string,
];

const generateAccessiblePeopleFromResourceAiQueryKey = (
  extraQueryParams = {},
): AccessiblePeopleFromResourceAIKeyType => {
  return ['accessible-people-resource-ai', JSON.stringify(extraQueryParams)];
};

const queryParamsForAccesiblePeopleForResourceAI = {
  source: RMPermissionsApiParamType.ResourceAI,
};

export const useAccesiblePeopleForResourceAI = (
  queryOptions: UseQueryOptions<
    number[],
    unknown,
    number[],
    AccessiblePeopleFromResourceAIKeyType
  > = {},
): UseQueryResult<number[]> =>
  useQuery(
    generateAccessiblePeopleFromResourceAiQueryKey(
      queryParamsForAccesiblePeopleForResourceAI,
    ),
    () => ResourceManagmentApi.getAccesiblePeopleForResourceAI(),
    {
      ...queryOptions,
    },
  );

type AccessibleProjectsFromResourceRequestKeyType = [
  'accessible-projects-resource-request',
  string,
];

const generateAccessibleProjectsFromResourceRequestQueryKey = (
  extraQueryParams = {},
): AccessibleProjectsFromResourceRequestKeyType => {
  return [
    'accessible-projects-resource-request',
    JSON.stringify(extraQueryParams),
  ];
};

export const useAccesibleProjectsForResourceRequest = (
  queryOptions: UseQueryOptions<
    number[],
    unknown,
    number[],
    AccessibleProjectsFromResourceRequestKeyType
  > = {},
): UseQueryResult<number[]> =>
  useQuery(
    generateAccessibleProjectsFromResourceRequestQueryKey(),
    () => ResourceManagmentApi.getAccesibleProjectsForResourceRequest(),
    {
      ...queryOptions,
    },
  );
